import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from "../../Firebase/Config";
import { useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Avatar, 
  Box, 
  Menu, 
  MenuItem, 
  ListItemIcon,
  Divider,
  Fade,
  Chip,
  Badge,
  Tooltip,
  alpha,
  Button,
  Paper,
  CircularProgress,
  Select,
  FormControl,
  InputBase,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  useMediaQuery,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Logout,
  AdminPanelSettings,
  Settings,
  SupportAgent,
  Person,
  KeyboardArrowDown,
  Dashboard,
  NotificationsNone,
  HelpOutline,
  AccountCircle,
  Restaurant as RestaurantIcon,
  Store,
  ArrowDropDown,
  Check,
  Close as CloseIcon,
  Phone,
  Email,
  Business
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { collection, query, where, getDocs, getFirestore } from 'firebase/firestore';
import AddRestaurantFormPopup from '../PopUpAddResto';

const softGlow = keyframes`
  0% { box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1); }
  50% { box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2); }
  100% { box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1); }
`;

// Styled Select component
const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: 180,
  maxWidth: 260,
  height: 40,
  backgroundColor: alpha('#1976D2', 0.04),
  borderRadius: 8,
  [theme.breakpoints.down('sm')]: {
    minWidth: 140,
    maxWidth: 180,
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#1A237E',
    fontWeight: 500,
    fontSize: '0.85rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      padding: '6px 8px',
    },
  },
  '&:hover': {
    backgroundColor: alpha('#1976D2', 0.08),
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #1976D2',
  },
}));

// Mobile Restaurant Selector Dialog
function MobileRestaurantSelector({ open, onClose, restaurants, selectedRestaurant, onSelect, loading }) {
  const theme = useTheme();
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          borderRadius: 0,
          backgroundColor: '#F8F9FA'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #E0E0E0',
        backgroundColor: '#FFFFFF',
        py: 2
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A237E' }}>
          Sélectionner un restaurant
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : restaurants.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Store sx={{ fontSize: 60, color: '#9AA6B2', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#5F6B7A', mb: 2 }}>
              Aucun restaurant trouvé
            </Typography>
            <Button
              variant="contained"
              startIcon={<Store />}
              onClick={() => {
                onClose();
                // Trigger add restaurant from parent
                window.dispatchEvent(new CustomEvent('openAddRestaurant'));
              }}
              sx={{ mt: 1 }}
            >
              Ajouter un restaurant
            </Button>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {restaurants.map((restaurant) => (
              <ListItemButton
                key={restaurant.id}
                onClick={() => {
                  onSelect(restaurant.id);
                  onClose();
                }}
                sx={{
                  py: 2,
                  px: 2,
                  borderBottom: '1px solid #F0F0F0',
                  backgroundColor: selectedRestaurant?.id === restaurant.id ? alpha('#1976D2', 0.04) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha('#1976D2', 0.08),
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                  <Avatar sx={{ bgcolor: alpha('#1976D2', 0.1), color: '#1976D2' }}>
                    <RestaurantIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A237E' }}>
                        {restaurant.name || restaurant.restaurantName}
                      </Typography>
                      {restaurant.id === selectedRestaurant?.id && (
                        <Check sx={{ fontSize: 20, color: '#1976D2' }} />
                      )}
                    </Box>
                    {restaurant.address && (
                      <Typography variant="caption" sx={{ color: '#78909C', display: 'block', mb: 0.5 }}>
                        {restaurant.address}
                      </Typography>
                    )}
                    <Chip 
                      label={restaurant.status || (restaurant.active ? 'Actif' : 'En attente')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor: restaurant.active ? alpha('#4CAF50', 0.1) : alpha('#FF9800', 0.1),
                        color: restaurant.active ? '#4CAF50' : '#FF9800',
                      }}
                    />
                  </Box>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Mobile Menu Drawer
function MobileMenuDrawer({ open, onClose, user, restaurants, onNavigate, onLogout }) {
  const theme = useTheme();
  
  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: {
          width: '85%',
          maxWidth: 320,
          backgroundColor: '#FFFFFF',
          borderRadius: 0,
        }
      }}
    >
      {/* User Profile Header */}
      <Box sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #1E2A3A 0%, #2C3E50 100%)',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#1976D2',
              fontSize: 20,
              fontWeight: 600,
              color: '#FFFFFF',
              border: '2px solid rgba(255,255,255,0.2)',
            }}
          >
            {user?.prenom && user?.nom ? `${user.prenom[0]}${user.nom[0]}`.toUpperCase() : <AccountCircle />}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user?.prenom} {user?.nom}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={user?.role || 'Administrateur'} 
          size="small"
          icon={<AdminPanelSettings sx={{ fontSize: 14 }} />}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            '& .MuiChip-icon': { color: 'white' }
          }}
        />
      </Box>

      <List sx={{ py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { onNavigate('/'); onClose(); }}>
            <ListItemIcon><Dashboard sx={{ color: '#5F6B7A' }} /></ListItemIcon>
            <ListItemText primary="Tableau de bord" />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton onClick={() => { onNavigate('/resto'); onClose(); }}>
            <ListItemIcon><Store sx={{ color: '#5F6B7A' }} /></ListItemIcon>
            <ListItemText primary="Mes restaurants" />
            {restaurants.length > 0 && (
              <Chip 
                label={restaurants.length} 
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton onClick={() => { onNavigate('/profile'); onClose(); }}>
            <ListItemIcon><Person sx={{ color: '#5F6B7A' }} /></ListItemIcon>
            <ListItemText primary="Mon profil" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton onClick={() => { onNavigate('/ReclamationPage'); onClose(); }}>
            <ListItemIcon><SupportAgent sx={{ color: '#5F6B7A' }} /></ListItemIcon>
            <ListItemText primary="Support" />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1 }} />

        <ListItem disablePadding>
          <ListItemButton onClick={onLogout} sx={{ color: '#E53935' }}>
            <ListItemIcon><Logout sx={{ color: '#E53935' }} /></ListItemIcon>
            <ListItemText primary="Déconnexion" primaryTypographyProps={{ color: '#E53935' }} />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Footer Info */}
      <Box sx={{ 
        mt: 'auto', 
        p: 2, 
        borderTop: '1px solid #F0F0F0',
        backgroundColor: '#FAFAFA'
      }}>
        <Typography variant="caption" sx={{ color: '#9AA6B2', display: 'block', textAlign: 'center' }}>
          Optimizi v1.0
        </Typography>
        <Typography variant="caption" sx={{ color: '#9AA6B2', display: 'block', textAlign: 'center' }}>
          © 2024 Optimizi Solutions
        </Typography>
      </Box>
    </SwipeableDrawer>
  );
}

function CustomAppBar({ onMenuClick, user, onRestaurantChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationCount] = useState(3);
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [isAddRestaurantOpen, setIsAddRestaurantOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileRestaurantSelectorOpen, setIsMobileRestaurantSelectorOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  // Get restaurantId from localStorage if it exists
  const storedRestaurantId = localStorage.getItem('restaurantId');

  // Listen for add restaurant event
  useEffect(() => {
    const handleOpenAddRestaurant = () => {
      setIsAddRestaurantOpen(true);
    };
    
    window.addEventListener('openAddRestaurant', handleOpenAddRestaurant);
    
    return () => {
      window.removeEventListener('openAddRestaurant', handleOpenAddRestaurant);
    };
  }, []);

  // Simplified fetch restaurants effect
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (!user?.uid) return;
      
      setLoadingRestaurants(true);
      try {
        const db = getFirestore();
        const q = query(collection(db, 'restaurants'), 
          where('ownerId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const restaurantData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        setRestaurants(restaurantData);
        
        // Set selected restaurant based on stored ID or first restaurant
        if (restaurantData.length > 0) {
          let restaurantToSelect;
          
          if (storedRestaurantId) {
            const savedRestaurant = restaurantData.find(r => r.id === storedRestaurantId);
            if (savedRestaurant) {
              restaurantToSelect = savedRestaurant;
            } else {
              restaurantToSelect = restaurantData[0];
            }
          } else {
            restaurantToSelect = restaurantData[0];
          }
          
          setSelectedRestaurant(restaurantToSelect);
          
          // Update localStorage with the selected restaurant ID
          localStorage.setItem('restaurantId', restaurantToSelect.id);
          localStorage.setItem('selectedRestaurant', JSON.stringify(restaurantToSelect));
          
          // Notify parent component about the selected restaurant
          if (onRestaurantChange) {
            onRestaurantChange(restaurantToSelect.id);
          }
        }
        
      } catch (error) {
        console.error("Error fetching restaurants:", error);
      } finally {
        setLoadingRestaurants(false);
      }
    };

    fetchRestaurants();
  }, [user?.uid, onRestaurantChange, storedRestaurantId]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/');
    } catch (error) {
      console.error("Logout failed: ", error.message);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleRestaurantChange = (selectedId) => {
    const restaurant = restaurants.find(r => r.id === selectedId);
    
    if (restaurant) {
      setSelectedRestaurant(restaurant);
      
      // Update localStorage with the selected restaurant ID and full data
      localStorage.setItem('restaurantId', restaurant.id);
      localStorage.setItem('selectedRestaurant', JSON.stringify(restaurant));
      
      // Dispatch a custom event to notify other components about restaurant change
      window.dispatchEvent(new CustomEvent('restaurantChanged', { 
        detail: restaurant 
      }));
      
      // Notify parent component about the restaurant change
      if (onRestaurantChange) {
        onRestaurantChange(restaurant.id);
      }
    }
  };

  const handleOpenAddRestaurant = () => {
    setIsAddRestaurantOpen(true);
  };

  const handleCloseAddRestaurant = () => {
    setIsAddRestaurantOpen(false);
    // Refresh restaurants list after adding a new one
    const fetchRestaurants = async () => {
      if (!user?.uid) return;
      
      try {
        const db = getFirestore();
        const q = query(collection(db, 'restaurants'), 
          where('ownerId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const restaurantData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        setRestaurants(restaurantData);
        
        if (restaurantData.length > 0) {
          setSelectedRestaurant(restaurantData[0]);
          
          // Update localStorage with the new restaurant
          localStorage.setItem('restaurantId', restaurantData[0].id);
          localStorage.setItem('selectedRestaurant', JSON.stringify(restaurantData[0]));
          
          if (onRestaurantChange) {
            onRestaurantChange(restaurantData[0].id);
          }
          
          // Dispatch event for other components
          window.dispatchEvent(new CustomEvent('restaurantChanged', { 
            detail: restaurantData[0] 
          }));
        }
      } catch (error) {
        console.error("Error fetching restaurants:", error);
      }
    };
    
    fetchRestaurants();
  };

  const getInitials = () => {
    if (user?.prenom && user?.nom) {
      return `${user.prenom[0]}${user.nom[0]}`.toUpperCase();
    }
    return <AccountCircle sx={{ fontSize: 24 }} />;
  };

  // Help Dialog
  const HelpDialog = () => (
    <Dialog open={isHelpDialogOpen} onClose={() => setIsHelpDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline sx={{ color: '#1976D2' }} />
          <Typography variant="h6">Aide & Support</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone sx={{ fontSize: 18 }} /> Support Téléphonique
          </Typography>
          <Typography variant="body2" color="text.secondary">
            +216 70 000 000 (Lun-Ven, 9h-17h)
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email sx={{ fontSize: 18 }} /> Support Email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            support@optimizi.com
          </Typography>
        </Box>
        
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ fontSize: 18 }} /> Documentation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consultez notre guide d'utilisation pour plus d'informations
          </Typography>
          <Button 
            variant="outlined" 
            size="small" 
            sx={{ mt: 1 }}
            onClick={() => window.open('/help', '_blank')}
          >
            Voir la documentation
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsHelpDialogOpen(false)}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease',
          height: { xs: 56, sm: 64 },
          justifyContent: 'center'
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between',
            minHeight: { xs: '56px !important', sm: '64px !important' },
            px: { xs: 1.5, sm: 2, md: 3 }
          }}
        >
          {/* Left Section - Logo and Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {/* Menu Toggle Button - Only visible on mobile */}
            <IconButton
              onClick={() => setIsMobileMenuOpen(true)}
              edge="start"
              sx={{ 
                color: '#546E7A',
                display: { xs: 'flex', sm: 'none' },
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Menu Toggle Button for desktop sidebar */}
            <IconButton
              onClick={onMenuClick}
              edge="start"
              sx={{ 
                color: '#546E7A',
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
              <Box 
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/images/LogoOptimizi.png`}
                alt="Optimizi Logo"
                sx={{ 
                  width: { xs: 28, sm: 32 }, 
                  height: { xs: 32, sm: 36 },
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05))',
                }}
              />
              
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography 
                  variant="h6"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    letterSpacing: 0.3,
                    color: '#1A237E',
                    lineHeight: 1.2,
                  }}
                >
                  Optimizi
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#78909C',
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    fontWeight: 400,
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  Intelligent Solutions
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Section - Restaurant Selector and User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {/* Restaurant Selector - Desktop */}
            {!isMobile && (
              loadingRestaurants ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : restaurants.length > 0 ? (
                <FormControl variant="outlined" size="small" sx={{ minWidth: { sm: 200, md: 250 } }}>
                  <StyledSelect
                    value={selectedRestaurant?.id || ''}
                    onChange={(e) => handleRestaurantChange(e.target.value)}
                    displayEmpty
                    IconComponent={ArrowDropDown}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RestaurantIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#1976D2' }} />
                            <Typography variant="body2" sx={{ color: '#1A237E', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                              Sélectionner
                            </Typography>
                          </Box>
                        );
                      }
                      const restaurant = restaurants.find(r => r.id === selected);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RestaurantIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#1976D2' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1A237E', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                              {restaurant?.name || restaurant?.restaurantName}
                            </Typography>
                            {!isMobile && restaurant?.address && (
                              <Typography variant="caption" sx={{ color: '#78909C', display: 'block', fontSize: '0.65rem' }}>
                                {restaurant?.address?.substring(0, 30)}...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    }}
                  >
                    {restaurants.map((restaurant) => (
                      <MenuItem key={restaurant.id} value={restaurant.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <RestaurantIcon sx={{ fontSize: 20, color: '#1976D2' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1A237E' }}>
                              {restaurant.name || restaurant.restaurantName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#78909C', display: 'block' }}>
                              {restaurant.address?.substring(0, 40)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={restaurant.status || (restaurant.active ? 'Actif' : 'En attente')}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.6rem',
                                backgroundColor: restaurant.active ? alpha('#4CAF50', 0.1) : alpha('#FF9800', 0.1),
                                color: restaurant.active ? '#4CAF50' : '#FF9800',
                              }}
                            />
                            {restaurant.id === selectedRestaurant?.id && (
                              <Check sx={{ fontSize: 18, color: '#1976D2' }} />
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Store />}
                  onClick={handleOpenAddRestaurant}
                  sx={{
                    mr: 1,
                    borderColor: '#1976D2',
                    color: '#1976D2',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    '&:hover': {
                      borderColor: '#1565C0',
                      backgroundColor: alpha('#1976D2', 0.04),
                    }
                  }}
                >
                  {isMobile ? 'Ajouter' : 'Ajouter restaurant'}
                </Button>
              )
            )}

            {/* Mobile Restaurant Selector Button */}
            {isMobile && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestaurantIcon />}
                onClick={() => setIsMobileRestaurantSelectorOpen(true)}
                sx={{
                  borderColor: '#1976D2',
                  color: '#1976D2',
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.5,
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: '#1565C0',
                    backgroundColor: alpha('#1976D2', 0.04),
                  }
                }}
              >
                {selectedRestaurant ? (selectedRestaurant.name || selectedRestaurant.restaurantName)?.substring(0, 15) : 'Resto'}
              </Button>
            )}

            {/* Notification Bell */}
            <Tooltip title="Notifications" arrow placement="bottom">
              <IconButton 
                sx={{ 
                  color: '#546E7A',
                  '&:hover': { 
                    color: '#1976D2',
                    backgroundColor: alpha('#1976D2', 0.04),
                  },
                }}
              >
                <Badge 
                  badgeContent={notificationCount} 
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#1976D2',
                      color: '#FFFFFF',
                      fontSize: { xs: 8, sm: 10 },
                      fontWeight: 600,
                      minWidth: { xs: 14, sm: 16 },
                      height: { xs: 14, sm: 16 },
                    }
                  }}
                >
                  <NotificationsNone sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Help Icon */}
            <Tooltip title="Help & Support" arrow placement="bottom">
              <IconButton 
                onClick={() => setIsHelpDialogOpen(true)}
                sx={{ 
                  color: '#546E7A',
                  '&:hover': { 
                    color: '#1976D2',
                    backgroundColor: alpha('#1976D2', 0.04),
                  },
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                <HelpOutline sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>

            {/* User Avatar with Indicator - Desktop */}
            {!isMobile && (
              <Tooltip title="Account settings" arrow placement="bottom">
                <IconButton 
                  onClick={handleMenuOpen}
                  sx={{ 
                    p: 0.25,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#1976D2', 0.04),
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: '#1976D2',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#FFFFFF',
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                      }}
                    >
                      {getInitials()}
                    </Avatar>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#1A237E',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          lineHeight: 1.2
                        }}
                      >
                        {user?.prenom} {user?.nom}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#78909C',
                          fontSize: '0.6rem',
                          display: 'block',
                          lineHeight: 1
                        }}
                      >
                        {user?.role || 'Administrateur'}
                      </Typography>
                    </Box>
                    <KeyboardArrowDown 
                      sx={{ 
                        fontSize: 18,
                        color: '#78909C',
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                      }} 
                    />
                  </Box>
                </IconButton>
              </Tooltip>
            )}

            {/* Mobile User Avatar */}
            {isMobile && (
              <IconButton 
                onClick={handleMenuOpen}
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#1976D2',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#FFFFFF',
                  }}
                >
                  {getInitials()}
                </Avatar>
              </IconButton>
            )}

            {/* User Menu Dropdown - Desktop */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 200 }}
              PaperProps={{
                elevation: 3,
                sx: {
                  width: 280,
                  mt: 1,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E0E0E0',
                  borderRadius: 2,
                  overflow: 'visible',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.05)',
                  '& .MuiDivider-root': {
                    borderColor: '#EEEEEE',
                    margin: '8px 0'
                  },
                  '& .MuiMenuItem-root': {
                    py: 1.25,
                    px: 2.5,
                    color: '#2C3E50',
                    transition: 'all 0.15s ease',
                    fontSize: '0.9rem',
                    '&:hover': {
                      backgroundColor: alpha('#1976D2', 0.04),
                      '& .MuiListItemIcon-root': {
                        color: '#1976D2',
                      }
                    }
                  }
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* User Info Header */}
              <Box sx={{ 
                p: 2.5,
                pb: 2,
                backgroundColor: alpha('#1976D2', 0.02),
                borderBottom: '1px solid #EEEEEE',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: '#1976D2',
                      fontSize: 18,
                      fontWeight: 600,
                      color: '#FFFFFF',
                      boxShadow: '0 4px 8px rgba(25, 118, 210, 0.2)',
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} color="#1A237E">
                      {user?.prenom} {user?.nom}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#78909C', display: 'block', mt: 0.25 }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Chip 
                    label={user?.role || 'Administrateur'} 
                    size="small"
                    icon={<AdminPanelSettings sx={{ fontSize: 14 }} />}
                    sx={{
                      backgroundColor: alpha('#1976D2', 0.08),
                      border: '1px solid',
                      borderColor: alpha('#1976D2', 0.2),
                      color: '#1976D2',
                      height: 24,
                      fontSize: '0.7rem',
                      '& .MuiChip-icon': {
                        color: '#1976D2',
                        fontSize: 14
                      }
                    }}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Menu Items */}
              <MenuItem onClick={() => handleNavigation('/')}>
                <ListItemIcon>
                  <Dashboard fontSize="small" sx={{ color: '#78909C' }} />
                </ListItemIcon>
                <Typography>Tableau de bord</Typography>
              </MenuItem>

              <MenuItem onClick={() => handleNavigation('/resto')}>
                <ListItemIcon>
                  <Store fontSize="small" sx={{ color: '#78909C' }} />
                </ListItemIcon>
                <Typography>Mes restaurants</Typography>
                {restaurants.length > 0 && (
                  <Chip 
                    label={restaurants.length} 
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      minWidth: 20,
                      fontSize: '0.7rem',
                      backgroundColor: alpha('#1976D2', 0.1),
                      color: '#1976D2'
                    }}
                  />
                )}
              </MenuItem>

              <MenuItem onClick={() => handleNavigation('/profile')}>
                <ListItemIcon>
                  <Person fontSize="small" sx={{ color: '#78909C' }} />
                </ListItemIcon>
                <Typography>Mon profil</Typography>
              </MenuItem>

              <MenuItem onClick={() => handleNavigation('/ReclamationPage')}>
                <ListItemIcon>
                  <SupportAgent fontSize="small" sx={{ color: '#78909C' }} />
                </ListItemIcon>
                <Typography>Réclamation</Typography>
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleLogout} sx={{ color: '#E53935' }}>
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: '#E53935' }} />
                </ListItemIcon>
                <Typography color="#E53935" fontWeight={500}>Déconnexion</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <MobileMenuDrawer
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        user={user}
        restaurants={restaurants}
        onNavigate={handleNavigation}
        onLogout={handleLogout}
      />

      {/* Mobile Restaurant Selector Dialog */}
      <MobileRestaurantSelector
        open={isMobileRestaurantSelectorOpen}
        onClose={() => setIsMobileRestaurantSelectorOpen(false)}
        restaurants={restaurants}
        selectedRestaurant={selectedRestaurant}
        onSelect={handleRestaurantChange}
        loading={loadingRestaurants}
      />

      {/* Help Dialog */}
      <HelpDialog />

      {/* Add Restaurant Form Popup */}
      <AddRestaurantFormPopup 
        open={isAddRestaurantOpen}
        onClose={handleCloseAddRestaurant}
        user={user}
      />
    </>
  );
}

export default CustomAppBar;